import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API, authHeaders } from '../api';
import Layout from './Layout';
import { getRole } from '../auth';
import { ui } from '../ui';
import { colors } from '../theme';

function pluralizeChats(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'чат ждёт';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'чата ждут';
  return 'чатов ждут';
}

function pluralizeRequests(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'заявка ждёт';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'заявки ждут';
  return 'заявок ждут';
}

function SupportUnreadBanner({ count }) {
  const navigate = useNavigate();
  if (!count) return null;
  return (
    <div style={bannerStyles.banner} onClick={() => navigate('/tickets')}>
      <span>💬 {count} {pluralizeChats(count)} ответа в техподдержке</span>
      <span style={bannerStyles.arrow}>→</span>
    </div>
  );
}

function PhotoRequestsBanner({ count }) {
  const navigate = useNavigate();
  if (!count) return null;
  return (
    <div style={bannerStyles.banner} onClick={() => navigate('/photo-requests')}>
      <span>🖼️ {count} {pluralizeRequests(count)} рассмотрения — запросы на замену фото</span>
      <span style={bannerStyles.arrow}>→</span>
    </div>
  );
}

function pluralizeReports(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'открытая жалоба';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'открытые жалобы';
  return 'открытых жалоб';
}

function ReportsBanner({ count }) {
  const navigate = useNavigate();
  if (!count) return null;
  return (
    <div style={{ ...bannerStyles.banner, ...bannerStyles.dangerBanner }} onClick={() => navigate('/reports')}>
      <span>🚩 {count} {pluralizeReports(count)} на пользователей</span>
      <span style={bannerStyles.arrow}>→</span>
    </div>
  );
}

function NoOpenSlotsBanner({ show, text, to }) {
  const navigate = useNavigate();
  if (!show) return null;
  return (
    <div style={{ ...bannerStyles.banner, ...bannerStyles.dangerBanner }} onClick={() => navigate(to)}>
      <span>🚫 {text}</span>
      <span style={bannerStyles.arrow}>→</span>
    </div>
  );
}

const bannerStyles = {
  banner: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#3d2b12', border: `1px solid ${colors.warn}`, borderRadius: 10,
    padding: '14px 18px', marginBottom: 12, cursor: 'pointer', color: colors.warn,
    fontWeight: 600, fontSize: 14,
  },
  dangerBanner: { background: '#3a1c1c', border: `1px solid ${colors.danger}`, color: colors.danger },
  arrow: { fontSize: 16 },
};

const STATUS_LABEL = {
  incomplete: 'Незавершён', awaiting_interview: 'Ждёт интервью', no_show: 'Не явка',
  active: 'Активна', hidden_auto: 'Скрыта авто', hidden_manual: 'Скрыта лично', blocked: 'Заблокирована',
};

const STATUS_ICON = {
  incomplete: '📝', awaiting_interview: '📅', no_show: '🚫',
  active: '✅', hidden_auto: '💤', hidden_manual: '🙈', blocked: '⛔',
};

// Единый юнит ширины плитки — размер 1 занимает TILE_UNIT, 1.5/2 пропорционально
// шире (с учётом gap между плитками), чтобы плитки с более длинным лейблом/сабом
// не переносили текст на 3 строки и не выбивались из общей высоты ряда.
const TILE_UNIT = 168;
const TILE_GAP = 16;

// Каждая строка (цифра/описание/изменение) занимает фиксированную высоту вместо
// центрирования всего блока целиком — иначе у плиток без "изменения" контент
// центрируется иначе, и цифры/подписи съезжают относительно соседних плиток.
function Tile({ label, value, sub, size = 1, valueColor, icon }) {
  const width = TILE_UNIT * size + TILE_GAP * (size - 1);
  return (
    <div style={{ ...tileStyles.tile, width }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon}
        <div style={{ ...tileStyles.value, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
      </div>
      <div style={tileStyles.label}>{label}</div>
      <div style={{ ...tileStyles.sub, visibility: sub ? 'visible' : 'hidden' }}>{sub || ' '}</div>
    </div>
  );
}

function GenderIcon({ gender }) {
  return gender === 'M' ? (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="14" r="6" />
      <line x1="14.2" y1="9.8" x2="20" y2="4" />
      <polyline points="14,4 20,4 20,10" />
    </svg>
  ) : (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="6" />
      <line x1="12" y1="14" x2="12" y2="21" />
      <line x1="8" y1="18" x2="16" y2="18" />
    </svg>
  );
}

const tileStyles = {
  tile: {
    background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12,
    padding: '16px 20px', minHeight: 130, boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
  },
  value: { fontSize: 28, fontWeight: 700, lineHeight: '34px', height: 34, color: colors.accent, fontVariantNumeric: 'tabular-nums' },
  label: { fontSize: 13, lineHeight: '16px', height: 32, color: colors.textMuted, marginTop: 4, overflow: 'hidden' },
  sub: { fontSize: 11, lineHeight: '14px', height: 14, color: colors.textMuted, marginTop: 6, opacity: 0.8 },
};

function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: authHeaders() }).then(r => setStats(r.data));
  }, []);

  if (!stats) return <div style={ui.empty}>Загрузка…</div>;

  return (
    <>
      <SupportUnreadBanner count={stats.support_unread_count} />
      <PhotoRequestsBanner count={stats.photo_replacement_pending_count} />
      <ReportsBanner count={stats.reports_open_count} />
      <NoOpenSlotsBanner
        show={stats.slots_upcoming_open === 0}
        text="0 свободных слотов интервью ни у одного куратора — нужно открыть новые слоты"
        to="/interview-calendar"
      />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tile label="Пользователей всего" value={stats.users_total} sub={`+${stats.new_users_7d} за 7 дней`} />
        <Tile
          label="Мужчины" icon={<GenderIcon gender="M" />} value={stats.users_male}
          sub={stats.users_total ? `${Math.round((stats.users_male / stats.users_total) * 100)}%` : undefined}
        />
        <Tile
          label="Женщины" icon={<GenderIcon gender="F" />} value={stats.users_female}
          sub={stats.users_total ? `${Math.round((stats.users_female / stats.users_total) * 100)}%` : undefined}
        />
        <Tile label="Активных анкет" value={stats.users_by_status.active || 0} />
        <Tile label="Кураторов" value={stats.curators_total} sub={`${stats.curators_active} активных`} />
        <Tile label="Мэтчей всего" value={stats.matches_total} sub={`+${stats.matches_7d} за 7 дней`} />
        <Tile
          label="Viral Coefficient"
          value={stats.viral_coefficient == null ? '—' : stats.viral_coefficient.toFixed(2)}
          sub={`${stats.referred_users_total} по рефералке / ${stats.non_referred_users_total} без`}
          valueColor={stats.viral_coefficient == null ? undefined : (stats.viral_coefficient >= 1 ? colors.good : colors.danger)}
        />
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <Tile label="Искр в обороте" value={stats.sparks_in_circulation} />
        <Tile label="Искр куплено всего" value={stats.sparks_purchased_total} />
        <Tile label="Искр подарено" value={stats.sparks_gifted_total} sub="промокоды + интервью" size={1.5} />
        <Tile label="Искр потрачено на мэтчи" value={stats.sparks_spent_on_matches} />
        <Tile label="Выручка" value={`${stats.revenue_rub} ₽`} sub="оплаты со статусом succeeded" size={1.5} />
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <Tile label="Открытых тикетов" value={stats.tickets_open} />
        <Tile
          label="Свободных слотов интервью" value={stats.slots_upcoming_open} size={1.5}
          valueColor={stats.slots_upcoming_open === 0 ? colors.danger : undefined}
        />
      </div>

      <div style={ui.card}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Анкеты по статусам</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_LABEL).map(([key, label]) => (
            <div key={key} style={{ fontSize: 13, color: colors.textMuted }}>
              <span aria-hidden="true">{STATUS_ICON[key]}</span> {label}: <b style={{ color: colors.text }}>{stats.users_by_status[key] || 0}</b>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function CuratorDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/curator/stats`, { headers: authHeaders() }).then(r => setStats(r.data));
  }, []);

  if (!stats) return <div style={ui.empty}>Загрузка…</div>;

  return (
    <>
      <SupportUnreadBanner count={stats.support_unread_count} />
      <PhotoRequestsBanner count={stats.photo_replacement_pending_count} />
      <ReportsBanner count={stats.reports_open_count} />
      <NoOpenSlotsBanner
        show={stats.open_slots === 0}
        text="0 свободных слотов интервью — откройте новые слоты"
        to="/slots"
      />
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      <Tile label="Моих клиентов" value={stats.clients_total} sub={`${stats.clients_active} активных`} />
      <Tile label="Ждут интервью" value={stats.clients_awaiting_interview} />
      <Tile label="Будущих слотов" value={stats.future_slots_total} sub="всего, включая занятые" />
      <Tile label="Свободных слотов" value={stats.open_slots} valueColor={stats.open_slots === 0 ? colors.danger : undefined} />
      <Tile label="Открытых тикетов" value={stats.tickets_open} sub="общая очередь" />
      </div>
    </>
  );
}

export default function Dashboard() {
  const role = getRole();
  return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h1 style={ui.title}>Дашборд</h1>
      </div>
      {role === 'super_admin' ? <SuperAdminDashboard /> : <CuratorDashboard />}
    </Layout>
  );
}
